import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, ArrowLeft, CheckCircle2, HelpCircle } from 'lucide-react';
import EmotionGuide from '../components/EmotionGuide';
import { useNavigate } from 'react-router-dom';
import { emotions, Emotion } from '../data/emotions';
import { generateAnalysisReport, StreamCallbacks } from '../services/analysis';
import { statsStorage, localSettings } from '../services/persistentStorage';
import { checkVerificationStatus, cleanupExpiredVerification, markTestCodeAsUsed } from '../services/codeVerification';

export interface EmotionalMemoryPatternProps {}

const EmotionalMemoryPattern: React.FC<EmotionalMemoryPatternProps> = () => {
  const navigate = useNavigate();
  const [hasValidAccess, setHasValidAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [step, setStep] = useState<1 | 2>(() => {
    const savedStep = localStorage.getItem('emotionStep');
    return savedStep ? Number(savedStep) as 1 | 2 : 1;
  });
  
  const [leastEmotions, setLeastEmotions] = useState<Emotion[]>(() => {
    try {
      const saved = localStorage.getItem('leastEmotions');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // 从 emotions 数组中找到对应的情绪对象
          return parsed
            .map(p => emotions.find(e => e.word === p.word))
            .filter((e): e is Emotion => e !== undefined);
        }
      }
    } catch (error) {
      console.error('读取最无感情绪数据失败:', error);
    }
    return [];
  });
  
  const [mostEmotions, setMostEmotions] = useState<Emotion[]>(() => {
    try {
      const saved = localStorage.getItem('mostEmotions');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // 从 emotions 数组中找到对应的情绪对象
          return parsed
            .map(p => emotions.find(e => e.word === p.word))
            .filter((e): e is Emotion => e !== undefined);
        }
      }
    } catch (error) {
      console.error('读取最有感情绪数据失败:', error);
    }
    return [];
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // 清除本地存储的函数
  const clearLocalStorage = () => {
    localStorage.removeItem('emotionStep');
    localStorage.removeItem('leastEmotions');
    localStorage.removeItem('mostEmotions');
    localStorage.removeItem('hasNavigatedToReport');
    window.location.reload(); // 刷新页面
  };


  // 保存状态到本地存储
  useEffect(() => {
    localStorage.setItem('emotionStep', String(step));
    localStorage.setItem('leastEmotions', JSON.stringify(leastEmotions));
    localStorage.setItem('mostEmotions', JSON.stringify(mostEmotions));
  }, [step, leastEmotions, mostEmotions]);

  // 检查访问权限
  useEffect(() => {
    const checkAccess = async () => {
      try {
        // 检查是否为开放模式
        const isOpenMode = await localSettings.getTestModeOpen('emotional-memory-pattern');
        
        if (isOpenMode) {
          console.log('开放模式已启用，直接允许访问');
          setHasValidAccess(true);
        } else {
          // 非开放模式，需要验证测试码
          // 首先清理过期的验证状态
          await cleanupExpiredVerification();
          
          // 检查验证状态
          const verificationStatus = checkVerificationStatus();
          
          if (!verificationStatus.isVerified || verificationStatus.isExpired) {
            console.log('验证状态无效或已过期，重定向到测试码输入页面');
            navigate('/test-code?type=emotional-memory-pattern', { replace: true });
            return;
          }
          
          console.log('访问权限验证通过');
          setHasValidAccess(true);
        }
        
        // 记录进入测评次数
        try {
          await statsStorage.recordTestEntry('emotional-memory-pattern');
          console.log('已记录进入情绪记忆测评');
        } catch (error) {
          console.error('记录进入测评失败:', error);
        }
        
      } catch (error) {
        console.error('检查访问权限失败:', error);
        navigate('/test-code?type=emotional-memory-pattern', { replace: true });
      } finally {
        setIsCheckingAccess(false);
      }
    };
    
    checkAccess();
  }, [navigate]);

  const handleEmotionSelect = (emotion: Emotion) => {
    if (step === 1) {
      if (leastEmotions.some(e => e.word === emotion.word)) {
        setLeastEmotions(prev => prev.filter(e => e.word !== emotion.word));
      } else if (leastEmotions.length < 10) {
        setLeastEmotions(prev => [...prev, emotion]);
      }
    } else {
      if (mostEmotions.some(e => e.word === emotion.word)) {
        setMostEmotions(prev => prev.filter(e => e.word !== emotion.word));
      } else if (mostEmotions.length < 15 && !leastEmotions.some(e => e.word === emotion.word)) {
        setMostEmotions(prev => [...prev, emotion]);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      
      // 清除本地存储
      localStorage.removeItem('emotionStep');
      localStorage.removeItem('leastEmotions');
      localStorage.removeItem('mostEmotions');
      localStorage.removeItem('hasNavigatedToReport'); // 重置导航状态
      
      // 定义流式响应的回调函数
      const callbacks: StreamCallbacks = {
        onStart: () => {
          console.log('开始接收流式响应');
          // 在onStart时不跳转，保持等待弹窗
        },
        onToken: (token) => {
          // 检查是否已经跳转
          const hasNavigated = localStorage.getItem('hasNavigatedToReport') === 'true';
          
          // 收到token时的处理
          console.log('收到token，token长度:', token.length);
          console.log('token内容预览:', token.substring(0, 50));
          console.log('token类型:', typeof token);
          
          // 如果是第一个token且尚未跳转，则跳转到报告页面
          if (!hasNavigated) {
            console.log('首次收到token，准备跳转到报告页面');
            // 标记已经跳转，避免重复跳转
            localStorage.setItem('hasNavigatedToReport', 'true');
            
            // 收到第一个有意义的token时才跳转到报告页面
            setIsLoading(false); // 先关闭加载状态，避免闪烁
              
            // 确保token不为空
            if (token && token.trim() !== '') {
              console.log('token有效，跳转到报告页面');
              // 跳转到分析报告页面，传递初始token但设置isStreaming为false，避免重复请求
              navigate('/analysis-report', {
                state: {
                  leastEmotions,
                  mostEmotions,
                  analysisReport: token, // 传递已收到的token作为初始内容
                  isStreaming: false, // 设置为false，避免报告页面重新发起请求
                  requestAlreadyStarted: true // 添加标志表示请求已经开始
                },
                replace: true // 使用replace而不是push，避免导航历史问题
              });
            } else {
              console.warn('收到的token为空，等待有效token');
            }
          } else {
            // 已经跳转，继续处理token
            console.log('已跳转到报告页面，继续处理token');
            
            // 尝试通过window.postMessage向报告页面发送新token
            try {
              window.postMessage({
                type: 'NEW_TOKEN',
                token: token
              }, '*');
              console.log('已发送token到报告页面');
            } catch (error) {
              console.error('发送token到报告页面失败:', error);
            }
          }
        },
        onComplete: async (fullText) => {
          console.log('流式响应完成，总长度:', fullText.length);
          // 完成后的处理由报告页面负责
          setIsLoading(false);
          
          // 标记测试码为已使用，清除验证状态
          try {
            await markTestCodeAsUsed();
            console.log('测试码已标记为已使用，用户需要新的测试码才能再次测评');
          } catch (error) {
            console.error('标记测试码为已使用失败:', error);
          }
          
          // 通知报告页面流式生成已完成
          try {
            window.postMessage({
              type: 'STREAM_COMPLETE',
              fullText: fullText
            }, '*');
            console.log('已通知报告页面流式生成完成');
          } catch (error) {
            console.error('通知报告页面流式生成完成失败:', error);
          }
          
          // 清除导航标记，以便下次测试能正常工作
          localStorage.removeItem('hasNavigatedToReport');
        },
        onError: (error) => {
          console.error('流式响应出错:', error);
          setIsLoading(false);
        }
      };
      
      // 使用流式响应生成报告
      await generateAnalysisReport({
        leastEmotions,
        mostEmotions
      }, callbacks);
      
    } catch (error) {
      console.error('生成分析报告失败:', error);
      // 可以添加一个错误提示UI
      setIsLoading(false);
    }
  };

  // 如果正在检查访问权限，显示加载状态
  if (isCheckingAccess) {
    return (
      <div className="min-h-screen bg-[#3A7D78] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-white"
        >
          <Brain className="w-16 h-16 mx-auto mb-4 text-[#F5B6C1] animate-pulse" />
          <p className="text-xl">验证访问权限中...</p>
        </motion.div>
      </div>
    );
  }
  
  // 如果没有有效访问权限，不渲染内容（实际上会被重定向）
  if (!hasValidAccess) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#3A7D78] py-8 px-4 relative">
      {/* 返回按钮 */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-white hover:text-[#F5B6C1] transition-colors mb-8"
      >
        <ArrowLeft className="h-5 w-5" />
        返回首页
      </button>

      {/* 主要内容区域 */}
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 rounded-2xl p-8 border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
        >
          {/* 标题 */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <Brain className="h-8 w-8 text-[#F5B6C1]" />
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-white">情绪记忆与构型</h1>
                <button
                  onClick={() => setIsGuideOpen(true)}
                  className="text-white/60 hover:text-white transition-colors p-1"
                  title="查看感受词汇说明书"
                >
                  <HelpCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            {/* 步骤指示器 */}
            <div className="flex justify-center gap-8 mb-6">
              <div className={`flex items-center gap-2 ${step === 1 ? 'text-[#94A3B8]' : 'text-white/60'}`}>
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${step === 1 ? 'border-[#94A3B8]' : step === 2 ? 'border-white/60' : ''}`}>
                  {step > 1 ? <CheckCircle2 className="w-6 h-6" /> : '1'}
                </div>
                <span>选择最无感的词语</span>
              </div>
              <div className={`flex items-center gap-2 ${step === 2 ? 'text-[#F5B6C1]' : 'text-white/60'}`}>
                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${step === 2 ? 'border-[#F5B6C1]' : 'border-white/60'}`}>
                  2
                </div>
                <span>选择最有感的词语</span>
              </div>
            </div>
            <p className="text-white/80">
              {step === 1 ? '请选择5-10个你最无感或几乎从不会有的感受' : '请选择8-15个最能代表你常见感受的词语'}
            </p>
            <p className="text-white/60 text-sm mt-2">
              已选择 {step === 1 ? `${leastEmotions.length}/5-10` : `${mostEmotions.length}/8-15`} 个
            </p>
          </div>

          {/* 情感词选择区域 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {emotions.map((emotion) => (
              <button
                key={emotion.word}
                onClick={() => handleEmotionSelect(emotion)}
                className={`p-3 rounded-lg text-center transition-all ${(step === 1 ? leastEmotions : mostEmotions).includes(emotion)
                  ? (step === 1 ? 'bg-[#94A3B8]' : 'bg-[#F5B6C1]') + ' text-white'
                  : 'bg-white/5 text-white/80 hover:bg-white/10'
                  } ${((step === 1 && leastEmotions.length >= 10) || (step === 2 && mostEmotions.length >= 15)) && !(step === 1 ? leastEmotions : mostEmotions).includes(emotion) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={((step === 1 && leastEmotions.length >= 10) || (step === 2 && mostEmotions.length >= 15)) && !(step === 1 ? leastEmotions : mostEmotions).includes(emotion)}
              >
                <div className="font-medium">{emotion.word}</div>
                <div className="text-xs mt-1 opacity-60">{emotion.dimension}</div>
              </button>
            ))}
          </div>

          {/* 步骤1完成按钮 */}
          {step === 1 && leastEmotions.length >= 5 && (
            <div className="text-center pt-4">
              <button
                onClick={() => setStep(2)}
                className="bg-[#94A3B8] text-white px-8 py-3 rounded-lg hover:bg-[#94A3B8]/90 transition-colors"
              >
                下一步
              </button>
            </div>
          )}

          {/* 返回上一步按钮 */}
          {step === 2 && (
            <div className="text-center mb-6">
              <button
                onClick={() => setStep(1)}
                className="text-white/80 hover:text-[#94A3B8] transition-colors"
              >
                返回上一步修改选择
              </button>
            </div>
          )}

          {/* 提交按钮 */}
          {step === 2 && mostEmotions.length >= 8 && (
            <div className="text-center pt-8">
              <button
                onClick={handleSubmit}
                className="bg-[#F5B6C1] text-white px-8 py-3 rounded-lg hover:bg-[#F5B6C1]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading || mostEmotions.length < 8}
              >
                {isLoading ? '分析中...' : '生成我的分析报告'}
              </button>
            </div>
          )}

          {/* 加载提示模态框 */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-[#3A7D78] p-8 rounded-2xl text-white text-center shadow-lg border border-white/20 max-w-2xl w-full"
              >
                <Brain className="w-16 h-16 mx-auto mb-4 text-[#F5B6C1] animate-pulse" />
                <p className="text-xl font-semibold mb-2">大语魔正在计算和翻译你的脑子...</p>
                
                <p className="text-white/80">请耐心等待，它正在路上！</p>
              </motion.div>
            </motion.div>
          )}

          {/* 感受词汇说明书 */}
          <EmotionGuide isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
        </motion.div>
      </div>
    </div>
  );
};

export default EmotionalMemoryPattern;